import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@wds/box';
import { MobileScreen, TabletScreen } from '@sm/webassets';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';
import { getClientEnvironmentDetails } from '@sm/utils';
import AppDetailsActionButton from './AppDetailsActionButton';
import UninstallButton from './UninstallButton';
import useStyles from '../styles';

export const AppDetailsJumboHeader = ({ appDetails, refreshDetails, showCTA }) => {
  const { isMobile = false } = getClientEnvironmentDetails();
  const { appDetailsHeaderLogo, appDetailsHeaderLogoImg } = useStyles();

  return (
    <Box mb={3}>
      <TabletScreen>
        <Grid noBleeds>
          <Grid.Item md={3} sm={12} xs={12}>
            <Box className={appDetailsHeaderLogo} p={3}>
              <img
                className={appDetailsHeaderLogoImg}
                src={appDetails.logo}
                alt={`${appDetails.name} Logo`}
                width="100%"
                height="auto"
              />
            </Box>
          </Grid.Item>
          <Grid.Item>
            <Typography variant="hero1" weight="medium" lineHeight={1.1}>
              {appDetails.name}
            </Typography>
            <Typography variant="bodySm">
              by <a href={appDetails.details.websiteUrl}>{appDetails.details.publisher}</a>
            </Typography>
            <Box py={6}>
              <Typography variant="body">{appDetails.tagline}</Typography>
            </Box>
            <If condition={showCTA}>
              <Box>
                <AppDetailsActionButton appDetails={appDetails} stretched={isMobile} />
                <If condition={appDetails.installed && !appDetails.isIntegration}>
                  <UninstallButton
                    appId={appDetails.id}
                    onSuccess={() => refreshDetails()}
                    color="alt"
                    stretched={isMobile}
                  />
                </If>
              </Box>
            </If>
          </Grid.Item>
        </Grid>
      </TabletScreen>
      <MobileScreen>
        <Box>
          <Grid noBleeds>
            <Grid.Item sm={3} xs={3}>
              <Box className={appDetailsHeaderLogo} p={3}>
                <img
                  className={appDetailsHeaderLogoImg}
                  src={appDetails.logo}
                  alt={`${appDetails.name} Logo`}
                  width="100%"
                  height="auto"
                />
              </Box>
            </Grid.Item>
            <Grid.Item sm={9} xs={9}>
              <Typography variant="hero1" lineHeight={1.1}>
                {appDetails.name}
              </Typography>
              <Typography variant="bodySm">
                by <a href={appDetails.details.websiteUrl}>{appDetails.details.publisher}</a>
              </Typography>
            </Grid.Item>
          </Grid>

          <Box py={6}>
            <Typography variant="body">{appDetails.tagline}</Typography>
          </Box>
          <AppDetailsActionButton appDetails={appDetails} refreshDetails={refreshDetails} stretched />
          <Box mt={2}>
            <If condition={appDetails.installed && !appDetails.isIntegration}>
              <UninstallButton appId={appDetails.id} onSuccess={() => refreshDetails()} color="alt" stretched />
            </If>
          </Box>
        </Box>
      </MobileScreen>
    </Box>
  );
};

AppDetailsJumboHeader.propTypes = {
  appDetails: PropTypes.shape({
    id: PropTypes.string,
    isIntegration: PropTypes.bool,
    name: PropTypes.string,
    tagline: PropTypes.string,
    fullDescription: PropTypes.string,
    logo: PropTypes.string,
    installed: PropTypes.bool,
    details: PropTypes.shape({
      publisher: PropTypes.string,
      featureList: PropTypes.arrayOf(PropTypes.string),
      googleAnalyticsTrackingId: PropTypes.string,
      supportEmail: PropTypes.string,
      supportPhoneNumber: PropTypes.string,
      supportUrl: PropTypes.string,
      requirements: PropTypes.arrayOf(PropTypes.string),
      screenshots: PropTypes.arrayOf(PropTypes.string),
      privacyPolicyUrl: PropTypes.string,
      termsOfUseUrl: PropTypes.string,
      blogUrl: PropTypes.string,
      youtubeUrl: PropTypes.string,
      websiteUrl: PropTypes.string,
      installUrl: PropTypes.string,
      upgradeRequired: PropTypes.bool,
      optionalUpgrade: PropTypes.bool,
      scopes: PropTypes.arrayOf(PropTypes.string),
      link: PropTypes.string,
      linkType: PropTypes.string,
    }),
  }).isRequired,
  refreshDetails: PropTypes.func.isRequired,
  showCTA: PropTypes.bool,
};

AppDetailsJumboHeader.defaultProps = {
  showCTA: false,
};

export default AppDetailsJumboHeader;
